import React, { useEffect, useState } from "react";

const formatCurrency = (value) => {
  return `₹${new Intl.NumberFormat("en-IN").format(value || 0)}`;
};

const METRO_CITY_KEYWORDS = [
  "mumbai",
  "delhi",
  "new delhi",
  "bangalore",
  "bengaluru",
  "hyderabad",
  "chennai",
  "kolkata",
  "pune",
  "ahmedabad",
];

const isMetroCityOption = (label) => {
  const text = String(label || "").toLowerCase();
  return METRO_CITY_KEYWORDS.some((metro) => text.includes(metro));
};

export const isSingleSelectQuestion = (question) => {
  if (!question) return false;
  if (question.type === "radio") return true;
  if (question.type === "dropdown") return true;
  if (
    question.type === "range" &&
    Array.isArray(question.options) &&
    question.options.length > 0
  ) {
    return true;
  }
  if (question.type === "select") {
    const isMultiSelect =
      Boolean(question.multiSelect) ||
      (typeof question.maxSelect === "number" && question.maxSelect > 1);
    return !isMultiSelect;
  }
  return false;
};

export const isQuestionValuePresent = (question, answersMap = {}) => {
  if (!question) return false;
  const value = answersMap[question.id];
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "number") return Number.isFinite(value);
  return false;
};

export const isQuestionSelectionLimitReached = (question, value) => {
  if (!question) return false;
  if (isSingleSelectQuestion(question)) {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length === 1;
    if (typeof value === "string") return value.trim() !== "";
    if (typeof value === "number") return Number.isFinite(value);
    return Boolean(value);
  }

  const isMultiSelect =
    Boolean(question.multiSelect) ||
    (typeof question.maxSelect === "number" && question.maxSelect > 1);

  if (isMultiSelect) {
    const selectedValues = Array.isArray(value)
      ? value
      : value
        ? [value]
        : [];
    if (typeof question.maxSelect === "number" && question.maxSelect > 0) {
      return selectedValues.length >= question.maxSelect;
    }
  }

  return false;
};

function QuestionRenderer({ question, value, onChange }) {
  const [dropdownQuery, setDropdownQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (question?.type !== "dropdown") return;
    const selectedOption = question.options?.find((option) => option.value === value);
    setDropdownQuery(selectedOption?.label || "");
  }, [question, value]);

  if (!question) return null;

  if (question.type === "select") {
    const isMultiSelect =
      Boolean(question.multiSelect) ||
      (typeof question.maxSelect === "number" && question.maxSelect > 1);
    const selectedValues = Array.isArray(value)
      ? value
      : value
        ? [value]
        : [];
    const maxSelect = question.maxSelect;
    const selectionCount = isMultiSelect ? selectedValues.length : (value ? 1 : 0);

    return (
      <div>
        <div className="req-chip-wrap">
          {question.options?.map((option) => {
            const selected = isMultiSelect
              ? selectedValues.includes(option.value)
              : value === option.value;
            const maxReached =
              isMultiSelect &&
              typeof maxSelect === "number" &&
              selectionCount >= maxSelect &&
              !selected;
            return (
              <button
                key={option.value}
                type="button"
                className={`req-chip ${selected ? "is-selected" : ""} ${maxReached ? "is-disabled" : ""}`}
                onClick={() => {
                  if (!isMultiSelect) {
                    onChange(option.value);
                    return;
                  }

                  if (selected) {
                    onChange(selectedValues.filter((item) => item !== option.value));
                    return;
                  }

                  if (maxReached) return;
                  onChange([...selectedValues, option.value]);
                }}
              >
                {selected && <span className="req-chip-check">✓</span>}
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
        {isMultiSelect && typeof maxSelect === "number" ? (
          <p className="req-select-hint">
            Select up to {maxSelect} options ({selectionCount}/{maxSelect})
          </p>
        ) : null}
      </div>
    );
  }

  if (question.type === "dropdown") {
    const options = question.options || [];
    const isLocationDropdown = ["location_input", "location"].includes(
      String(question.apiType || "").toLowerCase(),
    );
    const sortedOptions = isLocationDropdown
      ? [...options].sort((a, b) => {
          const aMetro = isMetroCityOption(a.label);
          const bMetro = isMetroCityOption(b.label);
          if (aMetro !== bMetro) return aMetro ? -1 : 1;
          return String(a.label || "").localeCompare(String(b.label || ""), "en", {
            sensitivity: "base",
          });
        })
      : options;
    const q = dropdownQuery.trim().toLowerCase();
    const filteredOptions = !q
      ? sortedOptions
      : sortedOptions.filter((option) => option.label.toLowerCase().includes(q));

    return (
      <div className="req-dropdown-field">
        <div className="req-dropdown-input-row">
          <img
            src="/MapPinSimpleArea.svg"
            alt=""
            className="req-select-leading-icon"
          />
          <input
            type="text"
            className={`req-select-input ${!dropdownQuery ? "is-empty" : ""}`}
            value={dropdownQuery}
            onChange={(e) => {
              setDropdownQuery(e.target.value);
              onChange("");
              setIsDropdownOpen(true);
            }}
            onFocus={() => {setIsDropdownOpen(true); console.log("Dropdown opened");}}
            placeholder={question.placeholder || "Search"}
          />
          <button
            type="button"
            className={`req-select-caret-btn ${isDropdownOpen ? "is-open" : ""}`}
            onClick={() => {
              onChange("");
              setDropdownQuery("");
              setIsDropdownOpen((prev) => !prev);
            }}
            aria-label={isDropdownOpen ? "Close city options" : "Open city options"}
          >
            <img src="/CaretRight.svg" alt="" className="req-select-caret" />
          </button>
        </div>
        {isDropdownOpen ? (
          <div className="req-dropdown-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <button
                  key={`${option.value}-${index}`}
                  type="button"
                  className={`req-dropdown-option ${
                    value === option.value ? "is-selected" : ""
                  }`}
                  onClick={() => {
                    onChange(option.value);
                    setDropdownQuery(option.label);
                    setIsDropdownOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div className="req-dropdown-empty">No cities found</div>
            )}
          </div>
        ) : null}
      </div>
    );
  }

  if (question.type === "radio") {
    return (
      <div className="req-radio-wrap">
        {question.options?.map((option) => {
          const selected = value === option.value;
          return (
            <button
              type="button"
              key={option.value}
              className={`req-radio-item ${selected ? "is-selected" : ""}`}
              onClick={() => onChange(option.value)}
            >
              <span className="req-radio-dot" />
              <span className="req-radio-copy">
                <span className="req-radio-label">{option.label}</span>
                {option.subLabel ? (
                  <span className="req-radio-sub-label">{option.subLabel}</span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  if (question.type === "range") {
    if (question.options?.length) {
      const selectedOption = question.options.find((option) => option.value === value);
      const headingLabel =
        selectedOption?.label || question.helperLabel || "Select a budget";

      return (
        <div className="req-range-picker">
          <div className="req-range-heading">{headingLabel}</div>
          <p className="req-range-heading-subtext">Selected budget</p>
          <div className="req-range-chip-wrap">
            {question.options.map((option) => {
              const selected = value === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`req-range-chip ${selected ? "is-selected" : ""}`}
                  onClick={() => onChange(option.value)}
                >
                  {selected ? <span className="req-chip-check">✓</span> : null}
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    const rangeValue =
      typeof value === "number" ? value : question.min || 0;
    return (
      <div className="req-range-wrap">
        <div className="req-range-value">{formatCurrency(rangeValue)}</div>
        <input
          type="range"
          min={question.min}
          max={question.max}
          step={question.step || 1}
          value={rangeValue}
          onChange={(e) => onChange(Number(e.target.value))}
          className="req-range-input"
        />
        <div className="req-range-scale">
          <span>{formatCurrency(question.min || 0)}</span>
          <span>{formatCurrency(question.max || 0)}</span>
        </div>
      </div>
    );
  }

  if (question.type === "multiline") {
    return (
      <textarea
        className="req-textarea"
        rows={5}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        minLength={question.minLength}
        maxLength={question.maxLength}
        placeholder={question.placeholder || "Write here"}
      />
    );
  }

  return (
    <input
      className="req-text-input"
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      minLength={question.minLength}
      maxLength={question.maxLength}
      placeholder={question.placeholder || "Enter value"}
    />
  );
}

export default QuestionRenderer;
